import React, { useEffect, useMemo, useState } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { createTrainingVideo } from "../api/trainingVideos";
import { getGlobalFilter } from "../../../api/filters";
import { delivery_man, preseller, salesman, sdr } from "../../../utility/constant";
import Dropdown from "../components/Dropdown";
import TrainingVideoScheduleGrid from "../components/TrainingVideoScheduleGrid";
import {
  appendScheduleFlagsToForm,
  createEmptyScheduleFlags,
  TrainingVideoScheduleFlags,
} from "../utils/trainingVideoSchedule";

type VideoFileKey = "file" | "file_ar" | "file_ur" | "file_hi" | "file_bangla";
type VideoLanguage = "en" | "ar" | "ur" | "hi" | "bangla";

const LANGUAGE_OPTIONS: {
  value: VideoLanguage;
  label: string;
  field: VideoFileKey;
}[] = [
  { value: "en", label: "English", field: "file" },
  { value: "ar", label: "Arabic", field: "file_ar" },
  { value: "ur", label: "Urdu", field: "file_ur" },
  { value: "hi", label: "Hindi", field: "file_hi" },
  { value: "bangla", label: "Bangla", field: "file_bangla" },
];

const AddTrainingVideo = () => {
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [selectedSalesOffices, setSelectedSalesOffices] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<VideoLanguage>("en");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [scheduleFlags, setScheduleFlags] = useState<TrainingVideoScheduleFlags>(
    createEmptyScheduleFlags
  );
  const [formData, setFormData] = useState({
    title: "",
    video_start_date: "",
    video_end_date: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchGlobalFilters = async () => {
      try {
        setLoadingFilters(true);
        const response = await getGlobalFilter({
          regions: ["SOUTH", "Riyadh Plastic", "CENTRAL", "EAST", "NORTH", "WEST"],
          positions: [salesman, preseller, sdr, delivery_man],
        });
        setSalesOffices(response?.sales_offices || []);
        setChannels(response?.channels || []);
      } catch (error) {
        console.error("Failed to fetch sales offices:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchGlobalFilters();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "video_end_date" && formData.video_start_date && value < formData.video_start_date) {
      toast.error("End date cannot be before start date.");
      return;
    }

    if (name === "video_start_date" && formData.video_end_date && value > formData.video_end_date) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = ["video/mp4", "video/webm", "video/quicktime"];
    const maxSize = 500 * 1024 * 1024;

    if (
      !validTypes.includes(selectedFile.type) &&
      !selectedFile.name.match(/\.(mp4|webm|mov)$/i)
    ) {
      toast.error("Invalid file type. Only MP4, WEBM, and MOV are allowed.");
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error("File size exceeds the maximum limit of 500MB.");
      return;
    }

    setVideoFile(selectedFile);
  };

  const getSelectedFileField = (): VideoFileKey => {
    return (
      LANGUAGE_OPTIONS.find((lang) => lang.value === selectedLanguage)?.field ??
      "file"
    );
  };

  const salesOfficeOptions = useMemo(
    () =>
      salesOffices.map((office: any) => {
        const officeId = String(office.id ?? office.sales_office_id);
        const officeName = office.name ?? office.sales_office_name ?? officeId;
        return {
          value: officeId,
          name: `${officeName} (${officeId})`,
        };
      }),
    [salesOffices]
  );

  const channelOptions = useMemo(
    () =>
      channels.map((channel: any) => {
        const channelId = String(channel.id ?? channel.channel_id);
        const channelName = channel.name ?? channel.channel_name ?? channelId;
        return {
          value: channelId,
          name: `${channelName} (${channelId})`,
        };
      }),
    [channels]
  );

  const handleSalesOfficeChange = (e: {
    target: { name: string; value: string | string[] };
  }) => {
    setSelectedSalesOffices(
      Array.isArray(e.target.value) ? e.target.value : [e.target.value]
    );
  };

  const handleChannelChange = (e: {
    target: { name: string; value: string | string[] };
  }) => {
    setSelectedChannels(
      Array.isArray(e.target.value) ? e.target.value : [e.target.value]
    );
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const appendOptionalField = (
    form: FormData,
    key: string,
    value: string | number | undefined
  ) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      form.append(key, String(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emptyFields: string[] = [];
    if (!videoFile) emptyFields.push("video");
    if (!formData.title.trim()) emptyFields.push("title");

    setInvalidFields(emptyFields);
    if (emptyFields.length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    const form = new FormData();
    const fileField = getSelectedFileField();
    form.append(fileField, videoFile as File, videoFile?.name);

    form.append("title", formData.title.trim());
    appendScheduleFlagsToForm(form, scheduleFlags);
    appendOptionalField(form, "video_start_date", formData.video_start_date);
    appendOptionalField(form, "video_end_date", formData.video_end_date);

    if (selectedSalesOffices.length > 0) {
      form.append("sales_offices", JSON.stringify(selectedSalesOffices));
    }

    if (selectedChannels.length > 0) {
      form.append("channels", JSON.stringify(selectedChannels));
    }

    try {
      setLoading(true);
      const response = await createTrainingVideo(form);
      toast.success(response?.message || "Training video uploaded successfully.");
      setTimeout(() => {
        navigate(URL_MAPPING.TRAININGVIDEOS);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to upload training video:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to upload training video.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          Back
        </button>
        <h3 className="text-xl font-bold text-gray-700">Upload Training Video</h3>
      </div>

      <form onSubmit={handleSubmit} className="p-5 lg:w-2/3 sm:w-full space-y-4">
        <div>
          <label className="block font-semibold mb-1">Language *</label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value as VideoLanguage)}
            className={getInputClass("language")}
          >
            {LANGUAGE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-semibold mb-1">Video File *</label>
          <input
            type="file"
            accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
            onChange={handleFileChange}
            className={getInputClass("video")}
          />
          {videoFile?.name && (
            <p className="text-sm text-gray-600 mt-1">
              {videoFile.name} (
              {LANGUAGE_OPTIONS.find((l) => l.value === selectedLanguage)?.label})
            </p>
          )}
        </div>

        <div>
          <label className="block font-semibold mb-1">Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={getInputClass("title")}
          />
        </div>

        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="block font-semibold mb-1">Video Start Date</label>
            <input
              type="date"
              name="video_start_date"
              value={formData.video_start_date}
              max={formData.video_end_date || undefined}
              onChange={handleChange}
              className={getInputClass("video_start_date")}
            />
          </div>
          <div className="w-1/2">
            <label className="block font-semibold mb-1">Video End Date</label>
            <input
              type="date"
              name="video_end_date"
              value={formData.video_end_date}
              min={formData.video_start_date || undefined}
              onChange={handleChange}
              className={getInputClass("video_end_date")}
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold mb-1">Schedule</label>
          <p className="text-sm text-gray-500 mb-2">
            Pick the weeks and days separately. Each selected week applies to all
            selected days (e.g. Week 1 + Week 4 with Sun, Mon, Tue = both weeks
            on Sun, Mon, Tue).
          </p>
          <TrainingVideoScheduleGrid
            value={scheduleFlags}
            onChange={setScheduleFlags}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Sales Offices</label>
          <Dropdown
            name="sales_offices"
            value={selectedSalesOffices}
            onChange={handleSalesOfficeChange}
            options={salesOfficeOptions}
            className={getInputClass("sales_offices")}
            placeholder={
              loadingFilters ? "Loading sales offices..." : "Select sales offices"
            }
            searchable
            multiple
            disabled={loadingFilters || salesOfficeOptions.length === 0}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Channels</label>
          <Dropdown
            name="channels"
            value={selectedChannels}
            onChange={handleChannelChange}
            options={channelOptions}
            className={getInputClass("channels")}
            placeholder={
              loadingFilters ? "Loading channels..." : "Select channels"
            }
            searchable
            multiple
            disabled={loadingFilters || channelOptions.length === 0}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Upload Video {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default AddTrainingVideo;
